/**
 * AccessBoundary - Manifest-driven access control wrapper.
 *
 * This component wraps route content and evaluates access policies
 * from the manifest. It operates in two modes:
 *
 * - Shadow mode (enforce=false): Logs decisions without blocking.
 *   Use during migration to validate manifest policies match layout guards.
 *
 * - Enforce mode (enforce=true): Redirects on deny decisions.
 *   Controlled by VITE_ENFORCE_ACCESS_POLICIES env var.
 *
 * The layouts (SoloLayout, ShopLayout) still perform their own validation.
 * AccessBoundary acts as a secondary safety net driven by the manifest.
 */

import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { logger } from '@/shared/utils/logger';
import { evaluateAccess } from './evaluator';
import { manifest } from '../manifest';
import type { AccessContext } from './types';

interface AccessBoundaryProps {
  /** Access context from the workspace layout */
  context: AccessContext;
  /** Whether to enforce decisions (false = log-only shadow mode) */
  enforce?: boolean;
  /** Optional children to render instead of Outlet */
  children?: ReactNode;
}

/**
 * Read enforcement flag from environment.
 * Defaults to false (shadow mode) when not set.
 */
const isEnforcementEnabled = (): boolean => {
  try {
    return import.meta.env.VITE_ENFORCE_ACCESS_POLICIES === 'true';
  } catch {
    return false;
  }
};

/**
 * Match current pathname to a manifest route entry
 */
const matchRoute = (pathname: string) => {
  return manifest.find(route => {
    // Convert route path to a regex pattern
    const pattern = route.path
      .replace(/\*/g, '.*')
      .replace(/:[\w]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(pathname);
  });
};

export const AccessBoundary: FC<AccessBoundaryProps> = ({
  context,
  enforce,
  children,
}) => {
  const location = useLocation();

  // Resolve enforcement: explicit prop > env var > default false
  const shouldEnforce = enforce ?? isEnforcementEnabled();

  const decision = useMemo(() => {
    const matchedRoute = matchRoute(location.pathname);

    if (!matchedRoute) {
      logger.debug('[AccessBoundary] No manifest entry for path', {
        pathname: location.pathname,
      });
      return { type: 'allow' as const };
    }

    const result = evaluateAccess(matchedRoute.access, context);

    if (result.type !== 'allow') {
      logger.info(`[AccessBoundary] ${shouldEnforce ? 'ENFORCING' : 'SHADOW'} access decision`, {
        routeId: matchedRoute.id,
        pathname: location.pathname,
        decision: result.type,
        reason: 'reason' in result ? result.reason : undefined,
        redirectTo: 'path' in result ? result.path : undefined,
      });
    }

    return result;
  }, [location.pathname, context, shouldEnforce]);

  // Enforce mode: redirect on deny decisions
  if (shouldEnforce && decision.type === 'redirect') {
    return <Navigate to={decision.path} replace />;
  }

  if (shouldEnforce && decision.type === 'forbidden') {
    return <Navigate to="/unauthorized" replace />;
  }

  // Shadow mode or allowed: render children
  return children ? children : <Outlet />;
};
